import React, { useState, useEffect, useMemo } from 'react';
import { BlobReader, BlobWriter, ZipReader } from '@zip.js/zip.js';
import LoadingScreen from '../components/LoadingScreen';
import { useAuth } from '../contexts/AuthContext';

const API_BASE = process.env.REACT_APP_API_BASE || '/.netlify/functions/api';
const STORAGE_FOLDERS_URL = `${API_BASE}/storage/folders`;
const STORAGE_FILES_URL = `${API_BASE}/storage/files`;

const STORAGE_UPLOAD_INIT_URL = `${API_BASE}/storage/uploads/init`;
const STORAGE_UPLOAD_COMPLETE_URL = `${API_BASE}/storage/uploads/complete`;

const MAX_FILE_SIZE_BYTES = 500 * 1024 * 1024;
const LARGE_DOWNLOAD_THRESHOLD_BYTES = 100 * 1024 * 1024;
const CHUNK_RETRY_LIMIT = 3;
const MAX_ZIP_ENTRIES = 2000;
const MAX_ZIP_DEPTH = 20;

const normalizeZipPath = (rawPath) => {
  const path = String(rawPath || '').replace(/\\/g, '/');
  if (!path || path.includes('\0') || path.startsWith('/') || /^[a-zA-Z]:\//.test(path)) {
    throw new Error(`مسار غير آمن داخل ZIP: ${rawPath || 'بدون اسم'}`);
  }
  const segments = path.split('/').filter(Boolean);
  if (!segments.length || segments.some((segment) => segment === '.' || segment === '..')) {
    throw new Error(`مسار غير آمن داخل ZIP: ${rawPath}`);
  }
  if (segments.length > MAX_ZIP_DEPTH) {
    throw new Error(`عمق المجلدات يتجاوز الحد المسموح (${MAX_ZIP_DEPTH})`);
  }
  if (segments.some((segment) => segment.length > 200)) {
    throw new Error('يوجد اسم ملف أو مجلد أطول من الحد المسموح');
  }
  return segments;
};

const shouldIgnoreZipEntry = (segments) =>
  segments[0] === '__MACOSX' ||
  segments.some((segment) => segment === '.DS_Store' || segment === 'Thumbs.db');

const mimeTypeFromName = (name) => {
  const extension = String(name).split('.').pop()?.toLowerCase();
  const types = {
    pdf: 'application/pdf',
    txt: 'text/plain',
    csv: 'text/csv',
    json: 'application/json',
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    png: 'image/png',
    gif: 'image/gif',
    webp: 'image/webp',
    mp4: 'video/mp4',
    mp3: 'audio/mpeg',
    docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    zip: 'application/zip',
  };
  return types[extension] || 'application/octet-stream';
};

const sha256Hex = async (buffer) => {
  const digest = await window.crypto.subtle.digest('SHA-256', buffer);
  return Array.from(new Uint8Array(digest))
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('');
};

const wait = (milliseconds) =>
  new Promise((resolve) => window.setTimeout(resolve, milliseconds));

const formatDateTime = (value) => {
  if (!value) return '—';
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleString('ar-SA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '—';
  }
};

const formatBytes = (bytes) => {
  if (bytes == null || Number.isNaN(bytes)) return '—';
  const b = Number(bytes);
  if (b < 1024) return `${b} B`;
  const kb = b / 1024;
  if (kb < 1024) return `${kb.toFixed(1)} KB`;
  const mb = kb / 1024;
  if (mb < 1024) return `${mb.toFixed(1)} MB`;
  const gb = mb / 1024;
  return `${gb.toFixed(1)} GB`;
};

const fetchJSON = async (url, options = {}) => {
  const res = await fetch(url, {
    headers: {
      Accept: 'application/json',
      ...(options.headers || {}),
    },
    ...options,
  });

  const text = await res.text();
  if (!res.ok) {
    let message = text || `HTTP ${res.status}`;
    try {
      const parsed = JSON.parse(text);
      message = parsed?.message || parsed?.error || message;
    } catch {}
    throw new Error(message);
  }

  try {
    return JSON.parse(text);
  } catch {
    throw new Error('Invalid JSON response');
  }
};

const displayUser = (item) => {
  const nameLike =
    item?.uploaderUsername ||
    item?.uploaderName ||
    item?.createdUsername ||
    item?.createdByName ||
    null;

  if (nameLike) return String(nameLike);

  const email = item?.uploaderEmail || item?.createdBy || null;
  if (!email) return '—';

  const s = String(email);
  const at = s.indexOf('@');
  if (at > 0) return s.slice(0, at);
  return s;
};

const AutoBdi = ({ children }) => (
  <bdi style={{ unicodeBidi: 'isolate', direction: 'auto' }}>{children}</bdi>
);

const FileStoragePage = () => {
  const { isAuthenticated, user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [folders, setFolders] = useState([]);
  const [files, setFiles] = useState([]);
  const [currentFolder, setCurrentFolder] = useState(null);
  const [breadcrumbs, setBreadcrumbs] = useState([]);

  const [newFolderName, setNewFolderName] = useState('');
  const [uploadFile, setUploadFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [zipFile, setZipFile] = useState(null);
  const [isImportingZip, setIsImportingZip] = useState(false);
  const [zipProgress, setZipProgress] = useState(0);
  const [isCreatingFolder, setIsCreatingFolder] = useState(false);

  const [errorMsg, setErrorMsg] = useState('');
  const [infoMsg, setInfoMsg] = useState('');

  const [fileToDelete, setFileToDelete] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const [folderToDelete, setFolderToDelete] = useState(null);
  const [showFolderDeleteModal, setShowFolderDeleteModal] = useState(false);
  const [isDeletingFolder, setIsDeletingFolder] = useState(false);

  const [folderToRename, setFolderToRename] = useState(null);
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [renameFolderName, setRenameFolderName] = useState('');
  const [isRenamingFolder, setIsRenamingFolder] = useState(false);

  const currentFolderId = currentFolder?.id || null;

  const userRole = useMemo(() => {
    if (!user) return null;
    const directRole =
      user.role || user?.app_metadata?.role || user?.user_metadata?.role;
    return directRole ? String(directRole).toLowerCase() : null;
  }, [user]);

  const isAdmin = useMemo(() => {
    if (!isAuthenticated || !userRole) return false;
    return ['owner', 'developer', 'admin'].includes(userRole);
  }, [isAuthenticated, userRole]);

  const username = useMemo(() => {
    const u =
      user?.user_metadata?.username ||
      user?.user_metadata?.user_name ||
      user?.user_metadata?.full_name ||
      user?.user_metadata?.name ||
      user?.email ||
      null;

    if (!u) return null;

    const s = String(u);
    const at = s.indexOf('@');
    if (at > 0) return s.slice(0, at);
    return s;
  }, [user]);

  const resetMessages = () => {
    setErrorMsg('');
    setInfoMsg('');
  };

  const loadData = async (folderId = null) => {
    resetMessages();
    setLoading(true);
    try {
      const folderUrl =
        folderId && folderId !== 'root'
          ? `${STORAGE_FOLDERS_URL}?parentId=${encodeURIComponent(folderId)}`
          : STORAGE_FOLDERS_URL;

      const fileUrl =
        folderId && folderId !== 'root'
          ? `${STORAGE_FILES_URL}?folderId=${encodeURIComponent(folderId)}`
          : STORAGE_FILES_URL;

      const [foldersRes, filesRes] = await Promise.all([
        fetchJSON(folderUrl),
        fetchJSON(fileUrl),
      ]);

      const folderList = Array.isArray(foldersRes?.folders) ? foldersRes.folders : [];
      const fileList = Array.isArray(filesRes?.files) ? filesRes.files : [];

      setFolders(folderList);
      setFiles(fileList);
    } catch (err) {
      console.error(err);
      setErrorMsg(err.message || 'تعذّر تحميل البيانات');
      setFolders([]);
      setFiles([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setCurrentFolder(null);
    setBreadcrumbs([]);
    loadData(null);
  }, []);

  const handleOpenFolder = async (folder) => {
    resetMessages();
    setCurrentFolder(folder);
    setBreadcrumbs((prev) => {
      const exists = prev.find((b) => b.id === folder.id);
      if (exists) {
        const idx = prev.findIndex((b) => b.id === folder.id);
        return prev.slice(0, idx + 1);
      }
      return [...prev, { id: folder.id, name: folder.name }];
    });
    await loadData(folder.id);
  };

  const handleBackToBreadcrumb = async (crumbIndex) => {
    resetMessages();

    if (crumbIndex < 0) {
      setCurrentFolder(null);
      setBreadcrumbs([]);
      await loadData(null);
      return;
    }

    const target = breadcrumbs[crumbIndex];
    if (!target) return;

    const newTrail = breadcrumbs.slice(0, crumbIndex + 1);
    setBreadcrumbs(newTrail);
    setCurrentFolder(target);
    await loadData(target.id);
  };

  const handleCreateFolder = async (e) => {
    e.preventDefault();
    resetMessages();

    if (!isAdmin) {
      setErrorMsg('لا تملك صلاحية إنشاء المجلدات.');
      return;
    }

    const name = (newFolderName || '').trim();
    if (!name) {
      setErrorMsg('الرجاء إدخال اسم المجلد');
      return;
    }

    setIsCreatingFolder(true);
    try {
      const body = {
        name,
        parentId: currentFolderId || null,
        username: username || null,
      };

      await fetchJSON(STORAGE_FOLDERS_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      setNewFolderName('');
      setInfoMsg('تم إنشاء المجلد بنجاح');
      await loadData(currentFolderId);
    } catch (err) {
      console.error(err);
      setErrorMsg(err.message || 'تعذّر إنشاء المجلد');
    } finally {
      setIsCreatingFolder(false);
    }
  };

  const handleFileInputChange = (e) => {
    resetMessages();
    const file = e.target.files?.[0] || null;

    if (file && file.size > MAX_FILE_SIZE_BYTES) {
      setUploadFile(null);
      setErrorMsg(`حجم الملف كبير جدًا. الحد الأقصى: ${formatBytes(MAX_FILE_SIZE_BYTES)}.`);
      if (e.target) e.target.value = '';
      return;
    }

    setUploadFile(file);
  };

  const uploadChunkWithRetry = async ({ uploadId, uploadToken, chunkIndex, buffer, checksum }) => {
    let lastError;
    for (let attempt = 1; attempt <= CHUNK_RETRY_LIMIT; attempt += 1) {
      try {
        const response = await fetch(
          `${API_BASE}/storage/uploads/${encodeURIComponent(uploadId)}/chunks/${chunkIndex}`,
          {
            method: 'PUT',
            credentials: 'include',
            headers: {
              'Content-Type': 'application/octet-stream',
              'X-Upload-Token': uploadToken,
              'X-Chunk-SHA256': checksum,
            },
            body: buffer,
          }
        );
        if (response.ok) return;
        const payload = await response.json().catch(() => ({}));
        const error = new Error(payload?.message || payload?.error || `HTTP ${response.status}`);
        if (response.status >= 400 && response.status < 500 && response.status !== 429) throw error;
        lastError = error;
      } catch (error) {
        lastError = error;
      }
      if (attempt < CHUNK_RETRY_LIMIT) await wait(500 * (2 ** (attempt - 1)));
    }
    throw lastError || new Error('فشل رفع جزء من الملف');
  };

  const uploadBlobInChunks = async ({
    blob,
    filename,
    mimeType,
    folderId,
    onProgress = () => {},
  }) => {
    const initRes = await fetchJSON(STORAGE_UPLOAD_INIT_URL, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        filename,
        sizeBytes: blob.size,
        mimeType: mimeType || 'application/octet-stream',
        folderId: folderId || null,
        uploaderUsername: username || null,
      }),
    });

    if (initRes?.existing && initRes?.file) {
      onProgress(blob.size, blob.size);
      return { file: initRes.file, skipped: true };
    }

    const uploadId = initRes?.uploadId;
    const uploadToken = initRes?.uploadToken;
    const chunkSize = Number(initRes?.chunkSize);
    const chunkCount = Number(initRes?.chunkCount);
    const uploadedChunks = new Map(
      Array.isArray(initRes?.uploadedChunks)
        ? initRes.uploadedChunks.map((chunk) => [Number(chunk.index), String(chunk.sha256 || '')])
        : []
    );

    if (!uploadId || !uploadToken || !chunkSize || !Number.isInteger(chunkCount) || chunkCount < 0) {
      throw new Error('رد غير مكتمل من الخادم لبدء الرفع');
    }

    let completedBytes = 0;
    for (let index = 0; index < chunkCount; index += 1) {
      const start = index * chunkSize;
      const end = Math.min(start + chunkSize, blob.size);
      const buffer = await blob.slice(start, end).arrayBuffer();
      const checksum = await sha256Hex(buffer);
      if (uploadedChunks.get(index) !== checksum) {
        await uploadChunkWithRetry({
          uploadId,
          uploadToken,
          chunkIndex: index,
          buffer,
          checksum,
        });
      }
      completedBytes += buffer.byteLength;
      onProgress(completedBytes, blob.size);
    }

    const completed = await fetchJSON(STORAGE_UPLOAD_COMPLETE_URL, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'X-Upload-Token': uploadToken,
      },
      body: JSON.stringify({ uploadId }),
    });
    return { file: completed?.file, skipped: false };
  };

  const handleUploadFile = async (e) => {
    e.preventDefault();
    resetMessages();

    if (!isAdmin) {
      setErrorMsg('لا تملك صلاحية رفع الملفات.');
      return;
    }

    if (!uploadFile) {
      setErrorMsg('الرجاء اختيار ملف للرفع');
      return;
    }

    if (uploadFile.size > MAX_FILE_SIZE_BYTES) {
      setErrorMsg(`حجم الملف كبير جدًا. الحد الأقصى: ${formatBytes(MAX_FILE_SIZE_BYTES)}.`);
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);

    try {
      await uploadBlobInChunks({
        blob: uploadFile,
        filename: uploadFile.name,
        mimeType: uploadFile.type,
        folderId: currentFolderId,
        onProgress: (completedBytes, totalBytes) => {
          setUploadProgress(Math.floor((completedBytes / totalBytes) * 100));
        },
      });

      setUploadFile(null);
      if (e.target && e.target.reset) e.target.reset();
      setUploadProgress(0);

      await loadData(currentFolderId);
      setInfoMsg('تم الرفع بنجاح');
    } catch (err) {
      console.error(err);
      setErrorMsg(err.message || 'فشل الرفع');
      setUploadProgress(0);
    } finally {
      setIsUploading(false);
    }
  };

  const handleZipImport = async () => {
    resetMessages();
    if (!isAdmin) {
      setErrorMsg('لا تملك صلاحية استيراد الملفات.');
      return;
    }
    if (!zipFile) {
      setErrorMsg('اختر ملف ZIP أولًا');
      return;
    }
    if (zipFile.size > MAX_FILE_SIZE_BYTES) {
      setErrorMsg(`حجم ملف ZIP يتجاوز ${formatBytes(MAX_FILE_SIZE_BYTES)}.`);
      return;
    }

    setIsImportingZip(true);
    setZipProgress(0);
    let zipReader;
    try {
      zipReader = new ZipReader(new BlobReader(zipFile), {
        checkAmbiguity: true,
        strictness: 'strict',
      });
      const rawEntries = await zipReader.getEntries();
      if (!rawEntries.length) throw new Error('ملف ZIP فارغ');
      if (rawEntries.length > MAX_ZIP_ENTRIES) {
        throw new Error(`عدد العناصر داخل ZIP يتجاوز الحد (${MAX_ZIP_ENTRIES})`);
      }

      const entries = rawEntries.map((entry) => {
        const segments = normalizeZipPath(entry.filename);
        const unixType = Number(entry.unixMode || 0) & 0o170000;
        if (unixType === 0o120000) {
          throw new Error(`الروابط الرمزية غير مسموحة داخل ZIP: ${entry.filename}`);
        }
        return { entry, segments };
      }).filter(({ segments }) => !shouldIgnoreZipEntry(segments));

      const fileEntries = entries.filter(({ entry }) => !entry.directory);
      if (fileEntries.some(({ entry }) => entry.encrypted)) {
        throw new Error('ملفات ZIP المشفرة بكلمة مرور غير مدعومة');
      }

      const totalBytes = fileEntries.reduce((sum, { entry }) => {
        const size = Number(entry.uncompressedSize);
        if (!Number.isSafeInteger(size) || size < 0) throw new Error('حجم غير صالح داخل ZIP');
        if (size > MAX_FILE_SIZE_BYTES) {
          throw new Error(`الملف ${entry.filename} يتجاوز حد 500MB`);
        }
        return sum + size;
      }, 0);
      if (totalBytes > MAX_FILE_SIZE_BYTES) {
        throw new Error(`إجمالي الملفات بعد فك الضغط يتجاوز ${formatBytes(MAX_FILE_SIZE_BYTES)}`);
      }

      const folderIds = new Map([['', currentFolderId || null]]);
      const ensureDirectory = async (segments) => {
        let parentId = currentFolderId || null;
        let pathKey = '';
        for (const segment of segments) {
          pathKey = pathKey ? `${pathKey}/${segment}` : segment;
          if (folderIds.has(pathKey)) {
            parentId = folderIds.get(pathKey);
            continue;
          }
          const result = await fetchJSON(STORAGE_FOLDERS_URL, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              name: segment,
              parentId,
              username: username || null,
            }),
          });
          const folderId = result?.folder?.id;
          if (!folderId) throw new Error(`تعذر إنشاء المجلد: ${segment}`);
          folderIds.set(pathKey, folderId);
          parentId = folderId;
        }
        return parentId;
      };

      const directoryEntries = entries
        .filter(({ entry }) => entry.directory)
        .sort((a, b) => a.segments.length - b.segments.length);
      for (const { segments } of directoryEntries) {
        await ensureDirectory(segments);
      }

      let processedBytes = 0;
      let uploadedCount = 0;
      let skippedCount = 0;
      for (const { entry, segments } of fileEntries) {
        const filename = segments[segments.length - 1];
        const folderSegments = segments.slice(0, -1);
        const folderId = await ensureDirectory(folderSegments);
        const entrySize = Number(entry.uncompressedSize);

        setInfoMsg(`جاري فك ورفع: ${segments.join('/')}`);
        const mimeType = mimeTypeFromName(filename);
        const blob = await entry.getData(new BlobWriter(mimeType), {
          checkSignature: true,
        });
        if (!blob || blob.size !== entrySize) {
          throw new Error(`فشل التحقق من حجم الملف: ${segments.join('/')}`);
        }

        const uploadResult = await uploadBlobInChunks({
          blob,
          filename,
          mimeType,
          folderId,
          onProgress: (fileBytes) => {
            const overall = processedBytes + fileBytes;
            setZipProgress(totalBytes ? Math.floor((overall / totalBytes) * 100) : 100);
          },
        });
        processedBytes += entrySize;
        if (uploadResult.skipped) skippedCount += 1;
        else uploadedCount += 1;
        setZipProgress(totalBytes ? Math.floor((processedBytes / totalBytes) * 100) : 100);
      }

      setZipProgress(100);
      setZipFile(null);
      const zipInput = document.getElementById('zip-import-input');
      if (zipInput) zipInput.value = '';
      const completionMessage =
        `اكتمل استيراد ZIP: رُفع ${uploadedCount} ملف` +
        (skippedCount ? `، وتم تجاوز ${skippedCount} ملف موجود مسبقًا` : '');
      await loadData(currentFolderId);
      setInfoMsg(completionMessage);
    } catch (error) {
      console.error(error);
      setErrorMsg(error?.message || 'تعذر استيراد ملف ZIP');
    } finally {
      if (zipReader) await zipReader.close().catch(() => {});
      setIsImportingZip(false);
    }
  };

  const openRawFile = async (file) => {
    resetMessages();
    try {
      if (file.storageMode === 'chunked' && file.chunkCount > 0) {
        let writable = null;
        if (
          file.sizeBytes >= LARGE_DOWNLOAD_THRESHOLD_BYTES &&
          typeof window.showSaveFilePicker === 'function'
        ) {
          const handle = await window.showSaveFilePicker({
            suggestedName: file.name || 'download',
          });
          writable = await handle.createWritable();
        }

        const parts = [];
        for (let index = 0; index < file.chunkCount; index += 1) {
          const response = await fetch(
            `${API_BASE}/storage/files/${encodeURIComponent(file.id)}/chunks/${index}`,
            { credentials: 'include' }
          );
          if (!response.ok) throw new Error(`تعذر تنزيل الجزء ${index + 1}`);
          const buffer = await response.arrayBuffer();
          const expectedChecksum = response.headers.get('X-Chunk-SHA256');
          if (expectedChecksum && (await sha256Hex(buffer)) !== expectedChecksum) {
            throw new Error(`فشل التحقق من الجزء ${index + 1}`);
          }
          if (writable) await writable.write(buffer);
          else parts.push(buffer);
          setInfoMsg(`جاري تجهيز الملف… ${Math.round(((index + 1) / file.chunkCount) * 100)}%`);
        }

        if (writable) {
          await writable.close();
          setInfoMsg('تم تنزيل الملف بنجاح');
          return;
        }

        const blob = new Blob(parts, { type: file.mimeType || 'application/octet-stream' });
        const objectUrl = URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = objectUrl;
        anchor.download = file.name || 'download';
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
        window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
        setInfoMsg('تم تجهيز الملف للتنزيل');
        return;
      }

      const rawUrl = `/storage/files/${encodeURIComponent(file.id)}/raw`;
      const w = window.open(rawUrl, 'noopener,noreferrer');
      if (!w) window.location.href = rawUrl;
    } catch (err) {
      console.error(err);
      setErrorMsg('تعذّر فتح الملف');
    }
  };

  const openDeleteModal = (file) => {
    resetMessages();
    if (!isAdmin) return;
    setFileToDelete(file);
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    if (isDeleting) return;
    setShowDeleteModal(false);
    setFileToDelete(null);
  };

  const confirmDeleteFile = async () => {
    if (!fileToDelete) return;
    resetMessages();

    if (!isAdmin) {
      setErrorMsg('لا تملك صلاحية حذف الملفات.');
      return;
    }

    setIsDeleting(true);
    try {
      const url = `${STORAGE_FILES_URL}/${encodeURIComponent(fileToDelete.id)}`;
      await fetchJSON(url, { method: 'DELETE' });
      setInfoMsg('تم حذف الملف بنجاح');
      await loadData(currentFolderId);
      setShowDeleteModal(false);
      setFileToDelete(null);
    } catch (err) {
      console.error(err);
      setErrorMsg(err.message || 'تعذّر حذف الملف');
    } finally {
      setIsDeleting(false);
    }
  };

  const openFolderDeleteModal = (folder) => {
    resetMessages();
    if (!isAdmin) return;
    setFolderToDelete(folder);
    setShowFolderDeleteModal(true);
  };

  const closeFolderDeleteModal = () => {
    if (isDeletingFolder) return;
    setShowFolderDeleteModal(false);
    setFolderToDelete(null);
  };

  const confirmDeleteFolder = async () => {
    if (!folderToDelete) return;
    resetMessages();

    if (!isAdmin) {
      setErrorMsg('لا تملك صلاحية حذف المجلدات.');
      return;
    }

    setIsDeletingFolder(true);
    try {
      const url = `${STORAGE_FOLDERS_URL}/${encodeURIComponent(folderToDelete.id)}`;
      await fetchJSON(url, { method: 'DELETE' });
      setInfoMsg('تم حذف المجلد بنجاح');
      await loadData(currentFolderId);
      setShowFolderDeleteModal(false);
      setFolderToDelete(null);
    } catch (err) {
      console.error(err);
      setErrorMsg(err.message || 'تعذّر حذف المجلد');
    } finally {
      setIsDeletingFolder(false);
    }
  };

  const openRenameModal = (folder) => {
    resetMessages();
    if (!isAdmin) return;
    setFolderToRename(folder);
    setRenameFolderName(folder.name || '');
    setShowRenameModal(true);
  };

  const closeRenameModal = () => {
    if (isRenamingFolder) return;
    setShowRenameModal(false);
    setFolderToRename(null);
    setRenameFolderName('');
  };

  const confirmRenameFolder = async () => {
    if (!folderToRename) return;

    if (!isAdmin) {
      setErrorMsg('لا تملك صلاحية تعديل المجلدات.');
      return;
    }

    const name = (renameFolderName || '').trim();
    if (!name) {
      setErrorMsg('الرجاء إدخال اسم المجلد');
      return;
    }

    resetMessages();
    setIsRenamingFolder(true);
    try {
      const url = `${STORAGE_FOLDERS_URL}/${encodeURIComponent(folderToRename.id)}`;
      await fetchJSON(url, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name }),
      });
      setInfoMsg('تم تعديل اسم المجلد بنجاح');
      await loadData(currentFolderId);
      setShowRenameModal(false);
      setFolderToRename(null);
      setRenameFolderName('');
    } catch (err) {
      console.error(err);
      setErrorMsg(err.message || 'تعذّر تعديل اسم المجلد');
    } finally {
      setIsRenamingFolder(false);
    }
  };

  const hasItems = useMemo(() => {
    return (folders && folders.length > 0) || (files && files.length > 0);
  }, [folders, files]);

  const breadcrumbItems = useMemo(() => {
    return [
      { id: 'root', name: 'الجذر', index: -1 },
      ...breadcrumbs.map((b, i) => ({ ...b, index: i })),
    ];
  }, [breadcrumbs]);

  if (loading) return <LoadingScreen />;

  return (
    <>
      <div
        className="min-h-screen text-white bg-transparent relative z-10 pt-12 md:pt-12 px-4 md:px-8 md:mr-[80px]"
        dir="ltr"
      >
        <div className="text-center mb-8 md:mb-12">
          <h1 className="text-2xl md:text-4xl font-bold mb-2 md:mb-4">
            مكتبة المصادر
          </h1>
          <p className="text-gray-300 text-sm md:text-lg px-4">
            تصفّح وحمّل ملفات المقررات، ورتّب مصادرِك داخل مجلدات منظَّمة لسهولة الوصول
          </p>
        </div>

        <div
          className="mb-4 flex flex-wrap items-center justify-start gap-2 text-xs md:text-sm w-full"
          dir="rtl"
        >
          {breadcrumbItems.map((item, idx) => (
            <React.Fragment key={item.id}>
              <button
                type="button"
                onClick={() => handleBackToBreadcrumb(item.index)}
                className={`px-2 py-1 rounded-full border ${
                  idx === breadcrumbItems.length - 1
                    ? 'border-[#28e3da] text-[#28e3da]'
                    : 'border-gray-500 text-gray-300 hover:border-[#28e3da] hover:text-[#28e3da]'
                } transition-colors`}
              >
                <AutoBdi>{item.name}</AutoBdi>
              </button>

              {idx !== breadcrumbItems.length - 1 && (
                <span className="text-gray-500">/</span>
              )}
            </React.Fragment>
          ))}
        </div>

        {(errorMsg || infoMsg) && (
          <div className="mb-4 flex flex-col gap-2 text-right">
            {errorMsg && (
              <div className="text-sm text-red-400 bg-red-900/20 border border-red-500/50 rounded-lg px-3 py-2">
                {errorMsg}
              </div>
            )}
            {infoMsg && (
              <div className="text-sm text-emerald-300 bg-emerald-900/20 border border-emerald-500/40 rounded-lg px-3 py-2">
                {infoMsg}
              </div>
            )}
          </div>
        )}

        {isAuthenticated && isAdmin ? (
          <div className="mb-8 grid gap-4 md:grid-cols-2">
            <form
              onSubmit={handleCreateFolder}
              className="bg-[#0f1e2e] border border-[#28e3da40] rounded-2xl p-4 flex flex-col gap-3"
              dir="rtl"
            >
              <h2 className="text-sm md:text-base font-semibold mb-1">
                إنشاء مجلد جديد
              </h2>
              <p className="text-xs md:text-sm text-gray-400 mb-2">
                سيتم إنشاء المجلد داخل المجلد الحالي
                {currentFolder ? (
                  <>
                    : <AutoBdi>{currentFolder.name}</AutoBdi>
                  </>
                ) : (
                  ' (الجذر)'
                )}
                .
              </p>
              <input
                type="text"
                placeholder="اسم المجلد..."
                value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
                className="w-full bg-[#081423] border border-[#28e3da40] rounded-xl px-3 py-2 text-white placeholder-gray-400 focus:outline-none focus:border-[#28e3da] text-sm"
              />
              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={isCreatingFolder}
                  className="px-4 py-2 rounded-xl border border-[#EEEEEE] text-[#EEEEEE] text-xs md:text-sm font-semibold hover:bg-white/10 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                >
                  {isCreatingFolder ? 'جاري الإنشاء...' : 'إنشاء مجلد'}
                </button>
              </div>
            </form>

            <form
              onSubmit={handleUploadFile}
              className="bg-[#0f1e2e] border border-[#28e3da40] rounded-2xl p-4 flex flex-col gap-3"
              dir="rtl"
            >
              <h2 className="text-sm md:text-base font-semibold mb-1">
                رفع ملف إلى المجلد الحالي
              </h2>
              <p className="text-xs md:text-sm text-gray-400 mb-2">
                يدعم ملفات حتى 500MB مع تقسيم آمن، تحقق من سلامة البيانات، واستكمال الرفع عند انقطاعه.
              </p>
              <input
                type="file"
                onChange={handleFileInputChange}
                className="w-full text-xs md:text-sm text-gray-300 file:mr-2 file:px-3 file:py-1.5 file:rounded-lg file:border-0 file:bg-[#28e3da30] file:text-white file:text-xs md:file:text-sm cursor-pointer"
              />
              {uploadFile && (
                <div className="text-xs text-gray-300">
                  الملف المختار:{' '}
                  <span className="text-[#28e3da]">
                    <AutoBdi>{uploadFile.name}</AutoBdi>
                  </span>{' '}
                  ({formatBytes(uploadFile.size)})
                </div>
              )}

              {isUploading && uploadProgress >= 0 && (
                <>
                  <div className="w-full bg-[#081423] rounded-full h-2 overflow-hidden border border-[#28e3da40]">
                    <div
                      style={{ width: `${uploadProgress}%` }}
                      className="h-full bg-gradient-to-r from-[#28e3da] to-[#00ffff] transition-all duration-300"
                    />
                  </div>
                  <p className="text-xs text-[#28e3da] text-center">
                    {uploadProgress}% — جاري الرفع...
                  </p>
                </>
              )}

              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={isUploading}
                  className="px-4 py-2 rounded-xl border border-[#EEEEEE] text-[#EEEEEE] text-xs md:text-sm font-semibold hover:bg-white/10 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                >
                  {isUploading ? `${uploadProgress}%` : 'رفع الملف'}
                </button>
              </div>

              <div className="my-2 border-t border-[#28e3da30]" />
              <h3 className="text-sm md:text-base font-semibold">استيراد مجلدات من ZIP</h3>
              <p className="text-xs md:text-sm text-gray-400">
                يفك الضغط ويحافظ على ترتيب المجلدات. المجلد الموجود يُستخدم تلقائيًا، والملف الموجود بالاسم نفسه لا يتكرر.
              </p>
              <input
                id="zip-import-input"
                type="file"
                accept=".zip,application/zip"
                disabled={isImportingZip || isUploading}
                onChange={(event) => {
                  resetMessages();
                  const selected = event.target.files?.[0] || null;
                  if (selected && !selected.name.toLowerCase().endsWith('.zip')) {
                    setZipFile(null);
                    setErrorMsg('الملف المختار يجب أن يكون بصيغة ZIP');
                    event.target.value = '';
                    return;
                  }
                  setZipFile(selected);
                  setZipProgress(0);
                }}
                className="w-full text-xs md:text-sm text-gray-300 file:mr-2 file:px-3 file:py-1.5 file:rounded-lg file:border-0 file:bg-[#28e3da30] file:text-white file:text-xs md:file:text-sm cursor-pointer disabled:opacity-60"
              />
              {zipFile && (
                <div className="text-xs text-gray-300">
                  ملف ZIP: <span className="text-[#28e3da]"><AutoBdi>{zipFile.name}</AutoBdi></span>{' '}
                  ({formatBytes(zipFile.size)})
                </div>
              )}
              {isImportingZip && (
                <>
                  <div className="w-full bg-[#081423] rounded-full h-2 overflow-hidden border border-[#28e3da40]">
                    <div
                      style={{ width: `${zipProgress}%` }}
                      className="h-full bg-gradient-to-r from-[#28e3da] to-[#00ffff] transition-all duration-300"
                    />
                  </div>
                  <p className="text-xs text-[#28e3da] text-center">
                    {zipProgress}% — جاري فك الضغط وإنشاء المجلدات والرفع...
                  </p>
                </>
              )}
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={handleZipImport}
                  disabled={!zipFile || isImportingZip || isUploading}
                  className="px-4 py-2 rounded-xl border border-[#28e3da] text-[#28e3da] text-xs md:text-sm font-semibold hover:bg-[#28e3da15] disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                >
                  {isImportingZip ? `${zipProgress}%` : 'فك واستيراد ZIP'}
                </button>
              </div>
            </form>
          </div>
        ) : !isAuthenticated ? (
          <div className="mt-4 mb-8 text-center" dir="rtl">
            <p className="text-sm md:text-base text-gray-300 mb-2">
              يمكنك تصفّح وفتح المصادر. لإدارة المجلدات ورفع الملفات يلزم تسجيل الدخول بصلاحية مشرف.
            </p>
          </div>
        ) : (
          <div className="mt-4 mb-8 text-center" dir="rtl">
            <p className="text-sm md:text-base text-gray-300 mb-2">
              يمكنك تصفّح وفتح الملفات فقط. صلاحيات الإدارة متاحة للمشرفين.
            </p>
          </div>
        )}

        <div className="text-center mb-4">
          <p className="text-gray-300 text-sm md:text-base">
            المجلدات: {folders.length} — الملفات: {files.length}
            {currentFolder && (
              <span className="text-[#28e3da]">
                {' '}
                (داخل: <AutoBdi>{currentFolder.name}</AutoBdi>)
              </span>
            )}
          </p>
        </div>

        <div className="space-y-3 md:space-y-6 mb-10">
          {hasItems ? (
            <>
              {folders.map((folder) => (
                <div
                  key={folder.id}
                  className="bg-transparent border border-white rounded-xl md:rounded-2xl p-3 md:p-5 flex flex-col md:flex-row md:items-center gap-3 md:gap-5 cursor-pointer hover:bg-[#1a2a3a] transition-colors"
                  onClick={() => handleOpenFolder(folder)}
                  title="افتح المجلد"
                >
                  <div className="flex-shrink-0 flex items-center justify-center w-[60px] h-[60px] md:w-[80px] md:h-[80px] rounded-2xl bg-[#0f1e2e] border border-[#28e3da40]">
                    <svg width="36" height="36" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path
                        d="M3 7C3 5.89543 3.89543 5 5 5H9L11 7H19C20.1046 7 21 7.89543 21 9V17C21 18.1046 20.1046 19 19 19H5C3.89543 19 3 18.1046 3 17V7Z"
                        stroke="#28E3DA"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>

                  <div className="flex-1 text-right space-y-1.5 md:space-y-2">
                    <p className="text-white text-sm md:text-lg font-medium">
                      <AutoBdi>{folder.name}</AutoBdi>
                      <span className="text-gray-400 ml-2 text-xs md:text-sm">:اسم المجلد</span>
                    </p>
                    <p className="text-gray-300 text-xs md:text-sm">
                      {displayUser(folder)}
                      <span className="text-gray-400 ml-2 text-xs md:text-sm">:أنشئ بواسطة</span>
                    </p>
                    <p className="text-gray-300 text-xs md:text-sm">
                      <span className="text-gray-400 ml-2 text-xs md:text-sm">تاريخ الإنشاء:</span>
                      {formatDateTime(folder.createdAt)}
                    </p>
                  </div>

                  {isAuthenticated && isAdmin && (
                    <div className="flex flex-row md:flex-col gap-2 justify-end md:items-end">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          openRenameModal(folder);
                        }}
                        className="px-3 py-1.5 rounded-xl border border-[#28e3da80] text-xs md:text-sm text-[#28e3da] hover:bg-[#28e3da20] transition-colors"
                      >
                        تعديل الاسم
                      </button>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          openFolderDeleteModal(folder);
                        }}
                        className="px-3 py-1.5 rounded-xl border border-red-500/80 text-xs md:text-sm text-red-300 hover:bg-red-900/30 transition-colors"
                      >
                        حذف المجلد
                      </button>
                    </div>
                  )}
                </div>
              ))}

              {files.map((file) => (
                <div
                  key={file.id}
                  className="bg-transparent border border-white rounded-xl md:rounded-2xl p-3 md:p-5 flex flex-col md:flex-row md:items-center gap-3 md:gap-5 hover:bg-[#1a2a3a] transition-colors cursor-pointer"
                  onClick={() => openRawFile(file)}
                  title="فتح / تنزيل"
                >
                  <div className="flex-shrink-0 flex flex-col items-center justify-center w-[80px] md:w-[100px]">
                    <div className="w-[60px] h-[60px] md:w-[72px] md:h-[72px] rounded-2xl bg-[#0f1e2e] border border-[#28e3da40] flex items-center justify-center mb-2">
                      <svg width="34" height="34" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                          d="M7 3H13L17 7V19C17 20.1046 16.1046 21 15 21H7C5.89543 21 5 20.1046 5 19V5C5 3.89543 5.89543 3 7 3Z"
                          stroke="#A2E9BD"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M13 3V7H17"
                          stroke="#A2E9BD"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>
                    <div className="text-xs text-gray-300">{formatBytes(file.sizeBytes)}</div>
                  </div>

                  <div className="flex-1 text-right space-y-1.5 md:space-y-2">
                    <p className="text-white text-sm md:text-lg font-medium">
                      <AutoBdi>{file.name}</AutoBdi>
                      <span className="text-gray-400 ml-2 text-xs md:text-sm">:اسم الملف</span>
                    </p>
                    <p className="text-gray-300 text-xs md:text-sm">
                      {file.mimeType || '—'}
                      <span className="text-gray-400 ml-2 text-xs md:text-sm">:النوع</span>
                    </p>
                    <p className="text-gray-300 text-xs md:text-sm">
                      {displayUser(file)}
                      <span className="text-gray-400 ml-2 text-xs md:text-sm">:مرفوع بواسطة</span>
                    </p>
                    <p className="text-gray-300 text-xs md:text-sm">
                      <span className="text-gray-400 ml-2 text-xs md:text-sm">تاريخ الرفع:</span>
                      {formatDateTime(file.createdAt)}
                    </p>
                  </div>

                  {isAuthenticated && (
                    <div className="flex flex-row md:flex-col gap-2 justify-end md:items-end">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          openRawFile(file);
                        }}
                        className="px-3 py-1.5 rounded-xl border border-[#28e3da80] text-xs md:text-sm text-[#28e3da] hover:bg-[#28e3da20] transition-colors"
                      >
                        فتح / تنزيل
                      </button>

                      {isAdmin && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            openDeleteModal(file);
                          }}
                          className="px-3 py-1.5 rounded-xl border border-red-500/80 text-xs md:text-sm text-red-300 hover:bg-red-900/30 transition-colors"
                        >
                          حذف
                        </button>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </>
          ) : (
            <div className="text-center py-12">
              <div className="text-gray-400 text-lg md:text-xl mb-2">
                لا توجد مجلدات أو ملفات هنا
              </div>
              <p className="text-gray-500 text-sm md:text-base">
                يمكنك إنشاء مجلد جديد أو رفع ملف في هذا المسار (للمشرفين فقط).
              </p>
            </div>
          )}
        </div>

        {showDeleteModal && fileToDelete && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
            <div className="w-full max-w-md bg-[#0f1e2e] border border-[#28e3da40] rounded-2xl p-6" dir="rtl">
              <h2 className="text-lg font-semibold mb-3 text-white">تأكيد حذف الملف</h2>
              <p className="text-sm text-gray-300 mb-6">
                هل أنت متأكد من حذف الملف{' '}
                <span className="text-[#28e3da] break-all">
                  <AutoBdi>{fileToDelete.name}</AutoBdi>
                </span>{' '}
                ؟ لا يمكن التراجع عن هذه العملية.
              </p>
              <div className="flex justify-between gap-3">
                <button
                  type="button"
                  onClick={closeDeleteModal}
                  disabled={isDeleting}
                  className="flex-1 px-4 py-2 rounded-xl border border-gray-500 text-sm text-gray-200 hover:bg-white/5 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                >
                  إلغاء
                </button>
                <button
                  type="button"
                  onClick={confirmDeleteFile}
                  disabled={isDeleting}
                  className="flex-1 px-4 py-2 rounded-xl border border-red-500/80 text-sm text-red-300 hover:bg-red-900/30 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                >
                  {isDeleting ? 'جاري الحذف...' : 'تأكيد الحذف'}
                </button>
              </div>
            </div>
          </div>
        )}

        {showFolderDeleteModal && folderToDelete && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
            <div className="w-full max-w-md bg-[#0f1e2e] border border-[#28e3da40] rounded-2xl p-6" dir="rtl">
              <h2 className="text-lg font-semibold mb-3 text-white">تأكيد حذف المجلد</h2>
              <p className="text-sm text-gray-300 mb-6">
                هل أنت متأكد من حذف المجلد{' '}
                <span className="text-[#28e3da] break-all">
                  <AutoBdi>{folderToDelete.name}</AutoBdi>
                </span>{' '}
                ؟ لا يمكن التراجع عن هذه العملية.
              </p>
              <div className="flex justify-between gap-3">
                <button
                  type="button"
                  onClick={closeFolderDeleteModal}
                  disabled={isDeletingFolder}
                  className="flex-1 px-4 py-2 rounded-xl border border-gray-500 text-sm text-gray-200 hover:bg-white/5 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                >
                  إلغاء
                </button>
                <button
                  type="button"
                  onClick={confirmDeleteFolder}
                  disabled={isDeletingFolder}
                  className="flex-1 px-4 py-2 rounded-xl border border-red-500/80 text-sm text-red-300 hover:bg-red-900/30 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                >
                  {isDeletingFolder ? 'جاري الحذف...' : 'تأكيد الحذف'}
                </button>
              </div>
            </div>
          </div>
        )}

        {showRenameModal && folderToRename && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
            <div className="w-full max-w-md bg-[#0f1e2e] border border-[#28e3da40] rounded-2xl p-6" dir="rtl">
              <h2 className="text-lg font-semibold mb-3 text-white">تعديل اسم المجلد</h2>
              <div className="mb-4">
                <label className="block text-sm text-gray-300 mb-2">الاسم الجديد للمجلد</label>
                <input
                  type="text"
                  value={renameFolderName}
                  onChange={(e) => setRenameFolderName(e.target.value)}
                  className="w-full bg-[#081423] border border-[#28e3da40] rounded-xl px-3 py-2 text-white placeholder-gray-400 focus:outline-none focus:border-[#28e3da] text-sm"
                  placeholder="اكتب اسم المجلد الجديد..."
                />
              </div>
              <div className="flex justify-between gap-3">
                <button
                  type="button"
                  onClick={closeRenameModal}
                  disabled={isRenamingFolder}
                  className="flex-1 px-4 py-2 rounded-xl border border-gray-500 text-sm text-gray-200 hover:bg-white/5 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                >
                  إلغاء
                </button>
                <button
                  type="button"
                  onClick={confirmRenameFolder}
                  disabled={isRenamingFolder}
                  className="flex-1 px-4 py-2 rounded-xl border border-[#28e3da80] text-sm text-[#28e3da] hover:bg-[#28e3da20] disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                >
                  {isRenamingFolder ? 'جاري الحفظ...' : 'حفظ التعديل'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default FileStoragePage;
